import { PPQ, barCapacity, ticks as durTicks, type Duration, type DurBase } from '@simplenotation/theory';
import type { Score, Beat, Note, BarInfo, TimeSignature, TempoMark, KeySignature } from '../../model/types';
import { SCORE_VERSION } from '../../model/types';
import { createTrack, newId, barRests, createRest } from '../../model/factories';
import { instrumentDef, type InstrumentDef } from '../../model/instruments';
import { validateScore, LIMITS } from '../../model/validate';
import { readSmf } from './smf';
import { instrumentForProgram, soundForProgram } from '../sounds';
import { writtenKey, spellInKey } from '../common';
import { fingerTrack } from '../fingering';

export interface MidiImportResult {
  score: Score;
  warnings: string[];
}

/** Parse a Standard MIDI File into a Score (quantized). */
export function importMidi(bytes: Uint8Array): Score {
  return importMidiWithWarnings(bytes).score;
}

interface RawNote {
  start: number;
  end: number;
  pitch: number;
  vel: number;
}

interface Group {
  track: number;
  channel: number;
  notes: RawNote[];
  program?: number;
}

interface BarSlot {
  start: number;
  len: number;
  ts: TimeSignature;
}

interface Chord {
  start: number;
  end: number;
  pitches: number[];
  vel: number;
}

const GRID = PPQ / 4;
const TRIPLET_GRID = PPQ / 3;

function decodeText(data: Uint8Array): string {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(data).replace(/\0+$/, '').trim();
  } catch {
    return new TextDecoder('latin1').decode(data).replace(/\0+$/, '').trim();
  }
}

/** Like importMidi, also returning the non-fatal problems found. */
export function importMidiWithWarnings(bytes: Uint8Array): MidiImportResult {
  const warnings: string[] = [];
  const warned = new Set<string>();
  const warn = (s: string) => {
    if (!warned.has(s)) {
      warned.add(s);
      warnings.push(s);
    }
  };
  const smf = readSmf(bytes);
  const scale = PPQ / smf.ppq;
  const tempoEv: { tick: number; us: number }[] = [];
  const tsEv: { tick: number; num: number; den: number }[] = [];
  const ksEv: { tick: number; sf: number; mi: number }[] = [];
  const markers: { tick: number; text: string }[] = [];
  let copyright: string | undefined;
  const trackNames: (string | undefined)[] = [];
  const groups = new Map<string, Group>();

  smf.tracks.forEach((events, ti) => {
    const open = new Map<number, { tick: number; vel: number }[]>();
    const programs = new Map<number, number>();
    let lastTick = 0;
    const groupOf = (ch: number): Group => {
      const key = `${ti}:${ch}`;
      let g = groups.get(key);
      if (!g) {
        g = { track: ti, channel: ch, notes: [] };
        groups.set(key, g);
      }
      return g;
    };
    for (const ev of events) {
      const tick = ev.tick * scale;
      lastTick = Math.max(lastTick, tick);
      if (ev.kind === 'meta') {
        const d = ev.data;
        if (ev.type === 0x51 && d.length >= 3) tempoEv.push({ tick, us: (d[0] << 16) | (d[1] << 8) | d[2] });
        else if (ev.type === 0x58 && d.length >= 2) tsEv.push({ tick, num: d[0], den: 2 ** d[1] });
        else if (ev.type === 0x59 && d.length >= 2) ksEv.push({ tick, sf: d[0] > 127 ? d[0] - 256 : d[0], mi: d[1] });
        else if (ev.type === 0x03 && trackNames[ti] === undefined) trackNames[ti] = decodeText(d);
        else if (ev.type === 0x02 && !copyright) copyright = decodeText(d);
        else if (ev.type === 0x06) markers.push({ tick, text: decodeText(d) });
      } else if (ev.kind === 'channel') {
        const key = ev.channel * 128 + ev.d1;
        if (ev.status === 0x90 && ev.d2 > 0) {
          const l = open.get(key) ?? [];
          l.push({ tick, vel: ev.d2 });
          open.set(key, l);
          const g = groupOf(ev.channel);
          if (g.program === undefined) g.program = programs.get(ev.channel) ?? 0;
        } else if (ev.status === 0x80 || (ev.status === 0x90 && ev.d2 === 0)) {
          const l = open.get(key);
          const on = l?.shift();
          if (on) groupOf(ev.channel).notes.push({ start: on.tick, end: Math.max(tick, on.tick + 1), pitch: ev.d1, vel: on.vel });
        } else if (ev.status === 0xc0) {
          if (!programs.has(ev.channel)) programs.set(ev.channel, ev.d1);
          const g = groups.get(`${ti}:${ev.channel}`);
          if (g && g.program === undefined) g.program = ev.d1;
        }
      }
    }
    for (const [key, l] of open) {
      for (const on of l) groupOf(Math.floor(key / 128)).notes.push({ start: on.tick, end: Math.max(on.tick + PPQ, lastTick), pitch: key % 128, vel: on.vel });
    }
  });

  const noteGroups = [...groups.values()].filter((g) => g.notes.length).sort((a, b) => a.track - b.track || a.channel - b.channel);
  if (!noteGroups.length) warn('The MIDI file contains no notes');
  const endTick = Math.max(0, ...noteGroups.flatMap((g) => g.notes.map((n) => n.end)));

  // ---- bars from time signatures
  tsEv.sort((a, b) => a.tick - b.tick);
  const validTs = (e: { num: number; den: number }): TimeSignature | null => {
    if (![1, 2, 4, 8, 16, 32, 64].includes(e.den) || e.num < 1 || e.num > 64) return null;
    return { num: e.num, den: e.den as DurBase };
  };
  const slots: BarSlot[] = [];
  {
    let cur: TimeSignature = { num: 4, den: 4 };
    let k = 0;
    let t = 0;
    while (slots.length === 0 || t < endTick - GRID / 2) {
      while (k < tsEv.length && tsEv[k].tick <= t + GRID / 2) {
        const v = validTs(tsEv[k]);
        if (v) cur = v;
        else warn(`Unsupported time signature ${tsEv[k].num}/${tsEv[k].den} ignored`);
        k++;
      }
      const len = barCapacity(cur);
      slots.push({ start: t, len, ts: cur });
      t += len;
      if (slots.length >= LIMITS.bars) {
        if (t < endTick - GRID / 2) warn(`Only the first ${LIMITS.bars} bars were imported`);
        break;
      }
    }
  }
  const nBars = slots.length;
  const totalLen = slots[nBars - 1].start + slots[nBars - 1].len;
  const barAt = (t: number): number => {
    let lo = 0;
    let hi = nBars - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (slots[mid].start <= t + 1e-6) lo = mid;
      else hi = mid - 1;
    }
    return lo;
  };

  const bars: BarInfo[] = slots.map(() => ({}));
  bars[0].timeSig = { ...slots[0].ts };
  for (let i = 1; i < nBars; i++) {
    const a = slots[i - 1].ts;
    const b = slots[i].ts;
    if (a.num !== b.num || a.den !== b.den) bars[i].timeSig = { ...b };
  }
  // A one-bar shorter first measure is a pickup.
  if (nBars >= 2 && bars[1].timeSig && (nBars < 3 || !bars[2].timeSig)) {
    const a = slots[0].ts;
    const b = slots[1].ts;
    if (a.den === b.den && a.num < b.num) {
      bars[0].timeSig = { ...b };
      bars[0].pickup = true;
      delete bars[1].timeSig;
    }
  }
  // Key signatures (concert).
  ksEv.sort((a, b) => a.tick - b.tick);
  for (const k of ksEv) {
    const sf = Math.max(-7, Math.min(7, k.sf));
    let b = barAt(k.tick);
    if (k.tick > slots[b].start + GRID / 2 && b + 1 < nBars) b++;
    bars[b].keySig = { fifths: sf, mode: k.mi === 1 ? 'minor' : 'major' };
  }
  if (!bars[0].keySig) bars[0].keySig = { fifths: 0, mode: 'major' };
  {
    let prev = bars[0].keySig!;
    for (let i = 1; i < nBars; i++) {
      const k = bars[i].keySig;
      if (!k) continue;
      if (k.fifths === prev.fifths && k.mode === prev.mode) delete bars[i].keySig;
      else prev = k;
    }
  }
  const concertKeyAt: KeySignature[] = [];
  {
    let k = bars[0].keySig!;
    for (let i = 0; i < nBars; i++) {
      if (bars[i].keySig) k = bars[i].keySig!;
      concertKeyAt.push(k);
    }
  }
  // Markers → sections.
  for (const m of markers) {
    if (!m.text) continue;
    const b = barAt(m.tick);
    if (!bars[b].section) bars[b].section = { name: m.text.slice(0, 64) };
  }
  // Tempo.
  tempoEv.sort((a, b) => a.tick - b.tick);
  const tempos: TempoMark[] = [];
  for (const e of tempoEv) {
    if (e.us <= 0) continue;
    let bpm = Math.round((60_000_000 / e.us) * 100) / 100;
    if (bpm < 5 || bpm > 1000) {
      warn(`Tempo ${bpm} clamped`);
      bpm = Math.max(5, Math.min(1000, bpm));
    }
    const b = barAt(e.tick);
    const off = Math.max(0, Math.min(slots[b].len - GRID, Math.round((e.tick - slots[b].start) / GRID) * GRID));
    const last = tempos[tempos.length - 1];
    if (last && last.bpm === bpm) continue;
    if (last && last.bar === b && last.beatOffsetTicks === off) {
      last.bpm = bpm;
      continue;
    }
    tempos.push({ bar: b, beatOffsetTicks: off, bpm, beatUnit: 4 });
  }
  if (!tempos.length || tempos[0].bar !== 0 || tempos[0].beatOffsetTicks !== 0) tempos.unshift({ bar: 0, beatOffsetTicks: 0, bpm: 120, beatUnit: 4 });
  bars[nBars - 1].barline = 'final';

  // ---- tracks
  const tracksOut = [];
  const multiChannel = new Set<number>();
  {
    const count = new Map<number, number>();
    for (const g of noteGroups) count.set(g.track, (count.get(g.track) ?? 0) + 1);
    for (const [t, c] of count) if (c > 1) multiChannel.add(t);
  }
  let groupsToUse = noteGroups;
  if (groupsToUse.length > LIMITS.tracks) {
    warn(`Only the first ${LIMITS.tracks} MIDI tracks/channels were imported`);
    groupsToUse = groupsToUse.slice(0, LIMITS.tracks);
  }
  // Quarter beats that may use a triplet grid: bars with den ≤ 4 starting on a quarter.
  const tripletEligible = (q: number): boolean => {
    const t = q * PPQ;
    if (t >= totalLen) return false;
    const s = slots[barAt(t)];
    return s.ts.den <= 4 && Math.abs(s.start / PPQ - Math.round(s.start / PPQ)) < 1e-9;
  };

  for (const g of groupsToUse) {
    const program = g.program ?? 0;
    const minPitch = Math.min(...g.notes.map((n) => n.pitch));
    const def: InstrumentDef = g.channel === 9 ? instrumentDef('drums') : instrumentForProgram(program, minPitch);
    const baseName = trackNames[g.track] || def.name.replace(/ \(.*\)$/, '');
    const track = createTrack(nBars, bars[0].timeSig!, { instrument: def.id, name: multiChannel.has(g.track) ? `${baseName} (ch ${g.channel + 1})` : baseName });
    track.sound = def.id === 'drums' ? 'drums' : sameFamilySound(soundForProgram(program), def);
    if (!track.tuning) delete track.tuning;
    if (track.capo === undefined) delete track.capo;
    delete track.shortName;

    // Triplet detection per quarter beat.
    const pointsByBeat = new Map<number, number[]>();
    for (const n of g.notes) {
      for (const x of [n.start, n.end]) {
        const q = Math.floor(x / PPQ + 1e-9);
        const l = pointsByBeat.get(q) ?? [];
        l.push(x - q * PPQ);
        pointsByBeat.set(q, l);
      }
    }
    const triplet = new Set<number>();
    for (const [q, pts] of pointsByBeat) {
      if (!tripletEligible(q)) continue;
      const inner = pts.filter((p) => p > GRID / 4 && p < PPQ - GRID / 4);
      if (inner.length < 1) continue;
      const err = (gr: number) => inner.reduce((a, p) => a + Math.abs(p - Math.round(p / gr) * gr), 0);
      const e16 = err(GRID);
      const e3 = err(TRIPLET_GRID);
      const off16 = Math.max(...inner.map((p) => Math.abs(p - Math.round(p / GRID) * GRID)));
      if (e3 < e16 * 0.5 && off16 >= GRID / 6) triplet.add(q);
    }
    const snap = (x: number): number => {
      const q = Math.floor(x / PPQ + 1e-9);
      const gr = triplet.has(q) ? TRIPLET_GRID : GRID;
      return Math.min(totalLen, q * PPQ + Math.round((x - q * PPQ) / gr) * gr);
    };
    const gridAt = (x: number) => (triplet.has(Math.floor(x / PPQ + 1e-9)) ? TRIPLET_GRID : GRID);

    // Quantize and build chords.
    const qn = g.notes
      .map((n) => {
        const s = snap(n.start);
        let e = snap(n.end);
        if (e <= s) e = Math.min(totalLen, s + gridAt(s));
        return { start: s, end: e, pitch: n.pitch, vel: n.vel };
      })
      .filter((n) => n.end > n.start && n.start < totalLen)
      .sort((a, b) => a.start - b.start || a.end - b.end || b.pitch - a.pitch);
    const chords: Chord[] = [];
    for (const n of qn) {
      const last = chords[chords.length - 1];
      if (last && last.start === n.start && last.end === n.end) {
        if (!last.pitches.includes(n.pitch)) last.pitches.push(n.pitch);
      } else chords.push({ start: n.start, end: n.end, pitches: [n.pitch], vel: n.vel });
    }
    const voices: Chord[][] = [[], []];
    const vEnd = [0, 0];
    for (const c of chords) {
      if (vEnd[0] <= c.start) {
        voices[0].push(c);
        vEnd[0] = c.end;
      } else if (vEnd[1] <= c.start) {
        voices[1].push(c);
        vEnd[1] = c.end;
      } else {
        const last = voices[0][voices[0].length - 1];
        if (last.start < c.start) {
          last.end = c.start;
          voices[0].push(c);
          vEnd[0] = c.end;
        } else {
          for (const p of c.pitches) if (!last.pitches.includes(p)) last.pitches.push(p);
          last.end = Math.min(last.end, c.end);
          vEnd[0] = last.end;
        }
      }
    }

    // Distribute into bars.
    const isTripletAt = (t: number) => triplet.has(Math.floor(t / PPQ + 1e-9));
    for (let v = 0; v < 2; v++) {
      const pieces: { bar: number; s: number; e: number; chord: Chord; last: boolean }[][] = Array.from({ length: nBars }, () => []);
      for (const c of voices[v]) {
        let s = c.start;
        while (s < c.end - 1e-6) {
          const b = barAt(s);
          const be = slots[b].start + slots[b].len;
          const e = Math.min(c.end, be);
          if (e <= s) break;
          pieces[b].push({ bar: b, s, e, chord: c, last: e >= c.end - 1e-6 });
          s = e;
          if (b === nBars - 1 && e >= be) break;
        }
      }
      for (let b = 0; b < nBars; b++) {
        const list = pieces[b];
        if (v === 1 && !list.length) continue;
        const slot = slots[b];
        const key = writtenKey(concertKeyAt[b], track);
        const beats: Beat[] = [];
        let cur = slot.start;
        const rests = (from: number, to: number) => {
          for (const d of segmentDurations(slot.start, from, to, isTripletAt)) beats.push(createRest(d));
        };
        if (!list.length) {
          track.bars[b].voices[0] = Math.abs(slot.len - barCapacity(slot.ts)) < 1e-6 ? barRests(slot.ts) : segmentDurations(slot.start, slot.start, slot.start + slot.len, isTripletAt).map((d) => createRest(d));
          continue;
        }
        for (const p of list) {
          if (p.s > cur + 1e-6) rests(cur, p.s);
          const durs = segmentDurations(slot.start, p.s, p.e, isTripletAt);
          durs.forEach((d, i) => {
            const finalPiece = p.last && i === durs.length - 1;
            const notes: Note[] = [];
            for (const m of p.chord.pitches.slice(0, LIMITS.notesPerBeat)) {
              const wm = m - track.writtenToSounding;
              const pitch = spellInKey(wm, key);
              if (pitch.octave < -1 || pitch.octave > 9) {
                warn(`Note ${m} is outside the supported range and was skipped`);
                continue;
              }
              const n: Note = { id: newId(), pitch };
              if (!finalPiece) n.tieToNext = true;
              notes.push(n);
            }
            if (p.chord.pitches.length > LIMITS.notesPerBeat) warn(`Chords with more than ${LIMITS.notesPerBeat} notes were truncated`);
            beats.push(notes.length ? { id: newId(), duration: d, rest: false, notes } : createRest(d));
          });
          cur = p.e;
        }
        if (cur < slot.start + slot.len - 1e-6) rests(cur, slot.start + slot.len);
        if (beats.length > LIMITS.beatsPerVoice) {
          warn(`More than ${LIMITS.beatsPerVoice} beats in a bar; the rest were dropped`);
          beats.length = LIMITS.beatsPerVoice;
        }
        if (v === 0) track.bars[b].voices[0] = beats;
        else track.bars[b].voices[1] = beats;
      }
    }
    // Dynamics from the average velocity of the first notes.
    const firstBeat = track.bars.flatMap((tb) => tb.voices[0]).find((b) => !b.rest);
    if (firstBeat && g.notes.length) {
      const avg = g.notes.slice(0, 16).reduce((a, n) => a + n.vel, 0) / Math.min(16, g.notes.length);
      const dyn = avg < 36 ? 'pp' : avg < 52 ? 'p' : avg < 68 ? 'mp' : avg < 88 ? 'mf' : avg < 106 ? 'f' : 'ff';
      if (dyn !== 'mf') firstBeat.dynamics = dyn;
    }
    if (track.tuning) fingerTrack(track);
    tracksOut.push(track);
  }
  if (!tracksOut.length) {
    const t = createTrack(nBars, bars[0].timeSig!, { instrument: 'piano' });
    if (!t.tuning) delete t.tuning;
    if (t.capo === undefined) delete t.capo;
    delete t.shortName;
    tracksOut.push(t);
  }
  const titleTrack = smf.format === 1 && trackNames[0] && !noteGroups.some((g) => g.track === 0) ? trackNames[0] : undefined;
  const score: Score = { v: SCORE_VERSION, meta: { title: titleTrack || 'Imported MIDI' }, bars, tempos, tracks: tracksOut };
  if (copyright) score.meta.copyright = copyright;
  const res = validateScore(score);
  if (!res.ok) throw new Error(`Imported score is invalid: ${res.error}`);
  return { score, warnings };
}

function sameFamilySound(sound: string, def: InstrumentDef): string {
  const fam = (s: string) => (s.startsWith('gtr_') ? 'gtr' : s.startsWith('bass_') ? 'bass' : s);
  return fam(sound) === fam(def.sound) ? sound : def.sound;
}

const PLAIN: Duration[] = (
  [
    [1, 1],
    [1, 0],
    [2, 1],
    [2, 0],
    [4, 1],
    [4, 0],
    [8, 1],
    [8, 0],
    [16, 1],
    [16, 0],
    [32, 1],
    [32, 0],
    [64, 0],
  ] as const
).map(([base, dots]) => ({ base: base as DurBase, dots: dots as 0 | 1 }));

/**
 * Durations for the span [s, e) (absolute ticks) of a bar starting at
 * `barStart`. Triplet quarter beats use triplet eighths/quarters; elsewhere
 * plain values aligned to their own length (capped at a quarter beat).
 */
export function segmentDurations(barStart: number, s: number, e: number, isTriplet: (t: number) => boolean): Duration[] {
  const out: Duration[] = [];
  let p = s;
  let guard = 0;
  while (p < e - 1e-6 && guard++ < 1000) {
    if (isTriplet(p)) {
      const beatStart = Math.floor(p / PPQ + 1e-9) * PPQ;
      const segEnd = Math.min(e, beatStart + PPQ);
      let len = segEnd - p;
      if (Math.abs(len - PPQ) < 1e-6) {
        out.push({ base: 4, dots: 0 });
        p = segEnd;
        continue;
      }
      while (len > 1e-6) {
        if (len >= 2 * TRIPLET_GRID - 1e-6) {
          out.push({ base: 4, dots: 0, tuplet: { actual: 3, normal: 2 } });
          len -= 2 * TRIPLET_GRID;
        } else if (len >= TRIPLET_GRID - 1e-6) {
          out.push({ base: 8, dots: 0, tuplet: { actual: 3, normal: 2 } });
          len -= TRIPLET_GRID;
        } else break;
      }
      p = segEnd;
      continue;
    }
    // Straight region up to the next triplet beat.
    let regionEnd = e;
    for (let q = Math.floor(p / PPQ + 1e-9) + 1; q * PPQ < e - 1e-6; q++) {
      if (isTriplet(q * PPQ)) {
        regionEnd = q * PPQ;
        break;
      }
    }
    while (p < regionEnd - 1e-6) {
      const rel = p - barStart;
      const left = regionEnd - p;
      let pick: Duration | null = null;
      for (const d of PLAIN) {
        const len = durTicks(d);
        if (len > left + 1e-6) continue;
        const undotted = (PPQ * 4) / d.base;
        const align = Math.min(undotted, PPQ);
        if (Math.abs(rel / align - Math.round(rel / align)) > 1e-6) continue;
        pick = d;
        break;
      }
      if (!pick) {
        pick = PLAIN.find((d) => durTicks(d) <= left + 1e-6) ?? { base: 64, dots: 0 };
        if (durTicks(pick) > left + 1e-6) {
          p = regionEnd;
          break;
        }
      }
      out.push(pick);
      p += durTicks(pick);
    }
  }
  return out;
}

